import { SignIn, SignedOut, SignedIn, useAuth as useClerkAuth } from "@clerk/clerk-react";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

// Paths that require admin / master / restricted permission at the router
// level (see App.js). If the user's last URL was one of these and their
// session expired, we DON'T want to deep-link them back after sign-in —
// the router guard would silently bounce them to "/", which surprises
// non-admin users and, more importantly, was causing the confusing "why
// am I on Settings when I sign in?" UX the ops team reported. For these
// paths we default to "/" instead and let the user navigate manually if
// they still have access.
const RESTRICTED_AFTER_SIGNIN_PATHS = [
  "/settings",
  "/audit",
  "/whatsapp",
  "/whatsapp-editor",
  "/members",
];

function isRestrictedTarget(path) {
  if (!path) return false;
  return RESTRICTED_AFTER_SIGNIN_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
}

/**
 * Login screen — sign-in only, no public signup. Users are added by the
 * admin inside the Clerk dashboard with a dummy password and a forced
 * reset on first sign-in. They must already be members of the African
 * Polistas Clerk organization (otherwise the backend rejects the token).
 */
export default function LoginPage() {
  const [sp] = useSearchParams();
  const rawNext = sp.get("next") || "/";
  // Only allow same-origin relative paths (defence against `?next=https://…`
  // open-redirects), and downgrade any admin-only path to "/" so we never
  // punt users into a page they can't render.
  const safeNext = rawNext.startsWith("/") && !rawNext.startsWith("//") && !isRestrictedTarget(rawNext)
    ? rawNext
    : "/";
  const wasForceSignedOut = sp.get("signed_out") === "role_changed";
  useEffect(() => {
    if (wasForceSignedOut) {
      // Toast on the /login page too, in case the previous page's toast
      // was wiped by the redirect. Duplicate-suppress by id.
      toast.info("Your permissions have changed. Please sign in again.", { id: "role-changed" });
    }
  }, [wasForceSignedOut]);
  return (
    <div className="min-h-screen flex items-center justify-center p-6"
      style={{ background: "hsl(var(--ap-paper))" }}>
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <img src="/logo.png" alt="African Polistas" className="h-14 w-14 rounded-xl mb-3 shadow-sm bg-white p-1" />
          <h1 className="ap-title-display" style={{ fontSize: "clamp(2rem, 5vw, 2.75rem)" }}>African Polistas</h1>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mt-1">Operations</p>
        </div>

        <SignedOut>
          <div id="clerk-captcha" />
          <SignIn
            forceRedirectUrl={safeNext}
            appearance={{
              elements: {
                rootBox: "mx-auto",
                card: "shadow-md",
                // Hide the "Don't have an account? Sign up" footer link
                footerAction: "hidden",
                footerActionLink: "hidden",
              },
            }}
            data-testid="clerk-signin"
          />
        </SignedOut>

        <SignedIn>
          <RedirectWhenSignedIn nextPath={safeNext} />
        </SignedIn>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Access is by invitation only. Contact your admin if you need an account.
        </p>
      </div>
    </div>
  );
}

function RedirectWhenSignedIn({ nextPath = "/" }) {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useClerkAuth();
  useEffect(() => {
    // Wait for Clerk to be fully loaded before redirecting — otherwise we
    // can bounce while the session is still being established.
    if (isLoaded && isSignedIn) {
      navigate(nextPath, { replace: true });
    }
  }, [isLoaded, isSignedIn, navigate, nextPath]);
  return <div className="text-center text-sm text-muted-foreground">Already signed in. redirecting…</div>;
}
